import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { imgUrl } from "@/lib/api";

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const t = document.createElement("textarea");
    t.value = text;
    document.body.appendChild(t);
    t.select();
    document.execCommand("copy");
    t.remove();
  }
};

const CopyBtn = ({ value, label, testId }) => {
  const [done, setDone] = useState(false);
  const click = async () => {
    await copyText(String(value));
    setDone(true);
    toast.success(`${label} copied`);
    setTimeout(() => setDone(false), 1800);
  };
  return (
    <button type="button" onClick={click} data-testid={testId}
      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border-2 border-primary text-primary text-xs font-bold hover:bg-primary hover:text-white active:scale-95 transition-[transform,background-color,color]">
      {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {done ? "Copied" : `Copy ${label}`}
    </button>
  );
};

const fmtAmount = (n) => Number(n || 0).toFixed(2).replace(/\.00$/, "");

export const UpiQr = ({ amount }) => {
  const { config } = useStore();
  if (!config?.upi_qr) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl bg-[#F9F8F4] p-4" data-testid="upi-qr-block">
      <img src={imgUrl(config.upi_qr)} alt="UPI QR code" className="w-56 sm:w-60 rounded-lg border border-stone-200 bg-white" data-testid="upi-qr-image" />
      <div className="text-sm space-y-2">
        <p className="font-bold text-primary">Scan to pay with any UPI app</p>
        <p className="text-stone-600 break-all">UPI ID: <b data-testid="upi-id-text">{config.upi_id}</b></p>
        {amount !== undefined && <p className="text-stone-600">Amount: <b data-testid="upi-amount-text">₹{fmtAmount(amount)}</b></p>}
        <div className="flex flex-wrap gap-2 pt-1">
          <CopyBtn value={config.upi_id} label="UPI ID" testId="copy-upi-id-btn" />
          {amount !== undefined && <CopyBtn value={fmtAmount(amount)} label="amount" testId="copy-amount-btn" />}
        </div>
      </div>
    </div>
  );
};

export const BankDetails = ({ amount }) => {
  const { config } = useStore();
  if (!config) return null;
  const rows = [["Account name", config.bank_account_name], ["Account no.", config.bank_account_number], ["IFSC", config.bank_ifsc], ["Branch", config.bank_name]];
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-[110px_1fr] gap-y-1.5 text-sm rounded-xl bg-[#F9F8F4] p-4" data-testid="bank-details">
        {rows.map(([k, v]) => (
          <div key={k} className="contents"><dt className="text-stone-500">{k}</dt><dd className="font-semibold text-primary break-all">{v}</dd></div>
        ))}
        <div className="col-span-2 flex flex-wrap gap-2 pt-2">
          <CopyBtn value={config.bank_account_number} label="account no." testId="copy-account-btn" />
          <CopyBtn value={config.bank_ifsc} label="IFSC" testId="copy-ifsc-btn" />
        </div>
      </dl>
      <p className="text-xs text-stone-500">Or pay the same amount via UPI:</p>
      <UpiQr amount={amount} />
    </div>
  );
};
